/**
 * The wire contract shared by the Worker, the upload site and the TV app.
 * Nothing in here may import runtime code — it is types + tiny pure helpers only,
 * so the TV bundle stays small.
 */

/* --------------------------------------------------------------- accounts -- */

export interface Account {
  readonly id: string;
  readonly email: string;
  readonly name: string;
  readonly createdAt: number;
}

export interface AuthResult {
  readonly token: string;
  readonly account: Account;
}

/* ---------------------------------------------------------------- billing -- */

/**
 * Where a frame stands with the money, as its owner's account decides it.
 *
 * - `live` — everything works. On a trial, or on a subscription in good
 *   standing.
 * - `grace` — the trial ran out or the subscription lapsed. Nothing sends into
 *   the frame, but every screen keeps showing what is already there for
 *   GRACE_DAYS, and says why. A frame going black because somebody's card
 *   expired lands on people who are not the person who pays, so the grace
 *   period is generous on purpose.
 * - `dormant` — the grace period ran out too. The frame comes off its screens.
 *   Nothing is deleted: the photos wait for the subscription to come back.
 *
 * `standingFor` in `packages/api/src/lib/billing.ts` is the only place this is
 * computed. Clients render it; they never recompute it.
 */
export type FrameStanding = 'live' | 'grace' | 'dormant';

/** A frame that nobody owns yet (the pre-accounts one) has nobody to bill. */
export const UNOWNED_STANDING: FrameStanding = 'live';

/** Every new account gets this long, and can make one frame during it. */
export const TRIAL_DAYS = 14;
/** How long a lapsed frame keeps showing before it goes dark. */
export const GRACE_DAYS = 30;
/** How many frames a trial covers. More is what a subscription is for. */
export const TRIAL_FRAME_LIMIT = 1;

export type BillingInterval = 'month' | 'year';

/**
 * The price list, in pence. One graduated price per interval: the first frame
 * at the higher rate, every additional one at the lower. Two frames a month is
 * therefore 4.99 + 2.99, not 4.99 twice, because the two-sides-of-the-family
 * case is the case the product is best at and should not be the dearest.
 *
 * The marketing site repeats these numbers in static HTML
 * (`packages/marketing/index.html`); a price change is a change in two places.
 */
export const PRICING: Record<
  BillingInterval,
  { readonly firstFrame: number; readonly additionalFrame: number }
> = {
  month: { firstFrame: 499, additionalFrame: 299 },
  year: { firstFrame: 4999, additionalFrame: 2999 },
};

/** Currency of every price above. Stripe shows local equivalents at checkout. */
export const PRICING_CURRENCY = 'GBP';

/** What `quantity` frames cost per interval, in pence. Zero frames is free. */
export const priceFor = (interval: BillingInterval, quantity: number): number => {
  const count = Math.max(0, Math.floor(quantity));
  if (count === 0) return 0;
  const tier = PRICING[interval];
  return tier.firstFrame + (count - 1) * tier.additionalFrame;
};

/** Pence to a display string, e.g. 499 -> "£4.99". */
export const formatPrice = (pence: number): string =>
  `£${(pence / 100).toFixed(2)}`;

/**
 * The Stripe subscription statuses this app treats as paid up. `trialing` is
 * here for completeness — trials are run by the app, not by Stripe, so it is
 * not expected — and `past_due` is not: the doc's rule is that a lapse stops
 * sending immediately and keeps showing for GRACE_DAYS, and Stripe's own retry
 * schedule runs inside that window.
 */
export type SubscriptionStatus =
  | 'active'
  | 'trialing'
  | 'past_due'
  | 'unpaid'
  | 'canceled'
  | 'incomplete'
  | 'incomplete_expired'
  | 'paused';

export interface SubscriptionView {
  readonly status: SubscriptionStatus;
  /** How many frames the subscription pays for. Stripe's `quantity`. */
  readonly frameLimit: number;
  readonly interval: BillingInterval;
  /** When the current paid period ends, epoch ms. */
  readonly currentPeriodEnd: number;
  /** True once cancellation has been asked for and the period is running out. */
  readonly cancelAtPeriodEnd: boolean;
}

/** What the billing page shows. All of it is computed on the server. */
export interface BillingSummary {
  readonly standing: FrameStanding;
  /**
   * How many frames this account may own right now, and how many it does.
   * `limit` is the subscription's quantity, or TRIAL_FRAME_LIMIT on a trial,
   * or 0 once neither applies.
   */
  readonly frameLimit: number;
  readonly framesOwned: number;
  /** When the trial ends, epoch ms. Always set; meaningless once subscribed. */
  readonly trialEndsAt: number;
  /**
   * When the current standing ends, epoch ms, or null when nothing is going to
   * change on its own: `live` on a trial ends at `trialEndsAt`, `grace` ends
   * when the frames go dark, `dormant` ends never.
   */
  readonly standingUntil: number | null;
  readonly subscription: SubscriptionView | null;
  /**
   * False when the deployment has no Stripe keys, in which case the page says
   * so and offers nothing. Every account is treated as live in that state, so
   * a deployment that has not set billing up is one that does not charge.
   */
  readonly enabled: boolean;
  /** Pence, for the plan picker. Repeated here so clients need no maths. */
  readonly pricing: typeof PRICING;
}

/**
 * How much of a device somebody has.
 *
 * - `owner` — created or claimed it. Only they can delete it, hand it over, or
 *   decide who else is a delegate.
 * - `delegate` — the household tier. Runs the frame day to day: settings,
 *   screens, guests, and the approval queue.
 * - `guest` — friends and family. Sends photos, subject to the permissions
 *   below, and sees nothing else.
 */
export type DeviceRole = 'owner' | 'delegate' | 'guest';

/**
 * What a given viewer may actually do, after the device-wide sharing flags and
 * the per-person ones have been folded together. The client never recomputes
 * this: the server is the only place the precedence rules live.
 */
export interface DevicePermissions {
  readonly canSend: boolean;
  /** False means photos land in the approval queue rather than the slideshow. */
  readonly autoAccept: boolean;
  /** Settings, screens, guests, moderation. Owner and delegates. */
  readonly canManage: boolean;
  /** Delete the device, change ownership, add or remove delegates. Owner only. */
  readonly canAdminister: boolean;
}

export interface DeviceSummary {
  readonly id: string;
  readonly label: string;
  readonly role: DeviceRole;
  readonly permissions: DevicePermissions;
  /**
   * The owner's billing state, as it applies to this frame. `canSend` above is
   * already false when this is not `live`; this is here so the client can say
   * *why* rather than just hide the button.
   */
  readonly standing: FrameStanding;
  /** Who to blame for the frame. Their own name if you are the owner. */
  readonly ownerName: string;
  readonly createdAt: number;
  readonly photoCount: number;
  /** Only meaningful for owners and delegates; 0 for everyone else. */
  readonly pendingCount: number;
  readonly screenCount: number;
}

export interface DeviceSharing {
  /** Anyone who knows the public link may send, with no account at all. */
  readonly publicUpload: boolean;
  /** Everything sent skips the approval queue, whoever sent it. */
  readonly autoAcceptEveryone: boolean;
  /** Absent until a link has been generated. */
  readonly publicUrl: string | null;
}

export interface DeviceDetail extends DeviceSummary {
  readonly sharing: DeviceSharing;
  readonly settings: SlideshowSettings;
}

export interface DeviceMember {
  readonly accountId: string;
  readonly name: string;
  readonly email: string;
  readonly role: DeviceRole;
  /** The stored per-person switches, as set on this row. */
  readonly canSend: boolean;
  readonly autoAccept: boolean;
  /** The same two after the device-wide flags are applied. */
  readonly effective: { readonly canSend: boolean; readonly autoAccept: boolean };
  readonly joinedAt: number;
}

export interface ShareInvite {
  readonly id: string;
  readonly label: string;
  readonly role: Exclude<DeviceRole, 'owner'>;
  readonly canSend: boolean;
  readonly autoAccept: boolean;
  readonly createdAt: number;
  readonly expiresAt: number | null;
  readonly usedCount: number;
}

/** What an invite link shows before you have decided whether to accept it. */
export interface InvitePreview {
  readonly deviceLabel: string;
  readonly role: Exclude<DeviceRole, 'owner'>;
  readonly invitedBy: string;
}

/** A paired television. One device may drive several. */
export interface Screen {
  readonly id: string;
  readonly label: string;
  readonly createdAt: number;
  readonly lastSeenAt: number | null;
}

/** What the public upload page is told about a frame before it lets you send. */
export interface PublicFrame {
  readonly deviceLabel: string;
  readonly ownerName: string;
  /** False once the owner turns the link off; the page then explains itself. */
  readonly open: boolean;
  /** False means "your photos wait for approval", which the page says up front. */
  readonly autoAccept: boolean;
}

/* ----------------------------------------------------------------- photos -- */

/** Metadata the browser derives from the file before it uploads. */
export interface PhotoMeta {
  /** SHA-256 of the *display* bytes, hex. Used for idempotent re-uploads. */
  readonly contentHash: string;
  readonly width: number;
  readonly height: number;
  /** EXIF DateTimeOriginal as epoch ms, if the file had one. */
  readonly takenAt: number | null;
  readonly caption: string | null;
  /**
   * Where it was taken, as somebody would say it out loud. Free text, never
   * coordinates — see 0004_location.sql. The apps read GPS out of the EXIF and
   * offer it as a suggestion, so this is only ever what a person accepted.
   */
  readonly location: string | null;
  readonly originalFilename: string;
}

/**
 * The text on a photo, which is the whole of what anyone may edit after the
 * fact. Both fields belong to the *placement*, so this always names one photo
 * on one frame, or one item in one album — never a picture in the abstract.
 *
 * `null` clears a field; the two are always sent together, so an edit is a
 * statement about both rather than a patch of one.
 */
export interface PhotoText {
  readonly caption: string | null;
  readonly location: string | null;
}

/** Long enough for a sentence, short enough to read from a sofa. */
export const MAX_CAPTION = 200;
/** A place name, not an address. */
export const MAX_LOCATION = 120;

/** Trim, cap, and treat an empty string as "not set" — the one rule for both
    fields, shared so the apps and the Worker cannot disagree about it. */
export const cleanText = (value: unknown, max: number): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed.slice(0, max) : null;
};

export type PhotoStatus = 'pending' | 'approved';

/**
 * Where a photo on a frame came from. `null` is an ordinary ad-hoc send; an
 * album means the placement is reconciled — it will leave the frame when it
 * leaves the album.
 */
export interface PhotoSource {
  readonly albumId: string;
  readonly albumLabel: string;
}

export interface PhotoRecord {
  /** The placement. Unique to this photo *on this frame*. */
  readonly id: string;
  /** The bytes behind it, shared by every placement of the same picture. */
  readonly assetId: string;
  readonly width: number;
  readonly height: number;
  readonly bytes: number;
  readonly takenAt: number | null;
  readonly uploadedAt: number;
  readonly uploader: string;
  readonly caption: string | null;
  readonly location: string | null;
  readonly status: PhotoStatus;
  readonly source: PhotoSource | null;
  /**
   * Whether *you* may edit the text on this placement. The server folds the
   * frame's permissions, who sent it and whether an album owns the text into
   * one answer, for the same reason `permissions` exists — the client renders
   * this, it does not recompute it.
   */
  readonly canEditText: boolean;
}

/** A photo plus the pre-signed URLs needed to render it. */
export interface PhotoView extends PhotoRecord {
  readonly url: string;
  readonly thumbUrl: string;
}

export interface UploadResult {
  readonly id: string;
  readonly duplicate: boolean;
  /** False when the photo went to the approval queue instead of the slideshow. */
  readonly accepted: boolean;
}

/** One frame's share of a send that went to several at once. */
export interface DeviceUploadResult extends UploadResult {
  readonly deviceId: string;
}

/**
 * The result of sending one picture to one or more frames. The bytes are stored
 * once — `assetId` names them — and each frame gets its own placement, its own
 * approval state and its own eventual deletion.
 */
export interface FanoutResult {
  readonly assetId: string;
  readonly results: readonly DeviceUploadResult[];
}

/* ----------------------------------------------------------------- albums -- */

/**
 * A living set of pictures, owned by a person. Publishing one to a frame does
 * not copy anything: it records the intent, and every later edit is replayed
 * onto every frame the album reaches.
 */
export interface AlbumSummary {
  readonly id: string;
  readonly label: string;
  readonly createdAt: number;
  readonly updatedAt: number;
  readonly itemCount: number;
  /** Frames it is currently showing on. */
  readonly publishedCount: number;
  /** First few thumbnails, for the card on the album list. */
  readonly coverUrls: readonly string[];
}

export interface AlbumPublication {
  readonly deviceId: string;
  readonly deviceLabel: string;
  readonly createdAt: number;
  /**
   * What this album's photos do when they land on that frame *now* — resolved
   * fresh on every reconcile, not frozen at publish time. A guest who loses
   * their send permission stops adding to the frame without anyone republishing.
   */
  readonly canSend: boolean;
  readonly autoAccept: boolean;
}

export interface AlbumDetail extends AlbumSummary {
  readonly publications: readonly AlbumPublication[];
}

export interface AlbumItemView {
  readonly assetId: string;
  readonly width: number;
  readonly height: number;
  readonly takenAt: number | null;
  readonly caption: string | null;
  readonly location: string | null;
  readonly addedAt: number;
  readonly thumbUrl: string;
}

/** One picture in somebody's library, independent of any frame it sits on. */
export interface AssetView {
  readonly id: string;
  readonly width: number;
  readonly height: number;
  readonly bytes: number;
  readonly takenAt: number | null;
  readonly createdAt: number;
  readonly thumbUrl: string;
}

/** What one frame did in response to an album change. */
export interface ReconcileSummary {
  readonly deviceId: string;
  readonly deviceLabel: string;
  /** Went straight onto the frame. */
  readonly added: number;
  /** Landed in that frame's approval queue instead. */
  readonly queued: number;
  /** Taken down because they left the album. */
  readonly removed: number;
  /**
   * True when the publication is live but the publisher may no longer send, so
   * nothing was added. The album stays published; it simply cannot push.
   */
  readonly blocked: boolean;
}

/* -------------------------------------------------------------- slideshow -- */

export interface SlideshowSettings {
  /** Seconds each photo is held. Kept low by default — see BURN-IN in README. */
  readonly dwellSeconds: number;
  /** Crossfade duration in ms. */
  readonly fadeMs: number;
  readonly shuffle: boolean;
  /** Slow Ken Burns drift. Also acts as OLED pixel-shift. */
  readonly kenBurns: boolean;
  readonly showCaption: boolean;
  /** 0–1 multiplier applied during quiet hours. */
  readonly nightDim: number;
  /** Local hour (0–23) the dimming starts / ends. Equal values disable it. */
  readonly nightFromHour: number;
  readonly nightToHour: number;
  /** How often the TV re-fetches the playlist, seconds. */
  readonly refreshSeconds: number;
}

export interface Playlist {
  readonly settings: SlideshowSettings;
  readonly photos: readonly PhotoView[];
  /** Changes whenever the photo set or settings change; lets the TV skip work. */
  readonly etag: string;
  /** Server clock, so the TV can apply quiet hours without a correct RTC. */
  readonly serverTime: number;
  /** The frame this screen is showing. Displayed while it has nothing else to. */
  readonly deviceLabel: string;
  /**
   * The frame's billing state. `grace` plays as normal and is worth a line on
   * the menu; `dormant` comes with an empty photo list, and the screen says why
   * instead of "no photos yet". Absent from playlists cached before this field
   * existed, which is why the frames read it with a fallback.
   */
  readonly standing?: FrameStanding;
}

export interface PairStart {
  readonly code: string;
  readonly pollToken: string;
  readonly expiresAt: number;
}

export type PairPoll =
  | { readonly status: 'pending' }
  | { readonly status: 'expired' }
  | { readonly status: 'approved'; readonly token: string };

export interface ApiError {
  readonly error: string;
  readonly message: string;
}

export const DEFAULT_SETTINGS: SlideshowSettings = {
  dwellSeconds: 20,
  fadeMs: 1400,
  shuffle: true,
  kenBurns: true,
  showCaption: true,
  nightDim: 0.55,
  nightFromHour: 22,
  nightToHour: 7,
  // The frame should pick up a photo while the sender is still in the room.
  // An unchanged poll costs one row read and no signing (see loadPlaylistStamp),
  // so the interval is bounded by taste rather than by cost.
  refreshSeconds: 5,
};

/**
 * webOS graphics planes top out at 1920x1080 even on UHD panels, so there is no
 * point shipping anything larger. Video is the only 4K surface.
 * https://webostv.developer.lge.com/develop/specifications/app-resolution
 */
export const DISPLAY_BOX = { width: 1920, height: 1080 } as const;
export const THUMB_BOX = { width: 400, height: 400 } as const;
export const MAX_DISPLAY_BYTES = 8 * 1024 * 1024;

/** Shortest password accepted at sign-up. Length is the only rule; see auth.ts. */
export const MIN_PASSWORD_LENGTH = 10;

export const isQuietHour = (
  hour: number,
  from: number,
  to: number,
): boolean => {
  if (from === to) return false;
  // Handles windows that wrap midnight, e.g. 22 -> 7.
  return from < to ? hour >= from && hour < to : hour >= from || hour < to;
};

/** Good enough to catch a typo, deliberately not RFC 5322. */
export const looksLikeEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

/* ------------------------------------------------------------------ admin -- */

export * from './admin';
